/**
 * ---------------------------------------------------------------------
 * VEU frontend-visitors app
 * ---------------------------------------------------------------------
 * @author seven-m.de
 * @copyright SEVEN M 2020
 */
/*
 * ---------------------------------------------------------------------
 * import modules
 * ---------------------------------------------------------------------
 */
import React, { Component } from 'react';
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Hidden } from '@material-ui/core';

import DeployEnvFrame from "src/shared_modules/react-components/pure/deploy-env-frame";

import MainNav from 'src/shared_modules/veu-visitors-core/components/mainNav';
import CookieOverlay from 'src/shared_modules/veu-visitors-core/components/cookie-overlay';
import PopoverProvider from 'src/shared_modules/veu-visitors-core/components/popovers/provider';
import ContentView from "src/shared_modules/veu-visitors-core/components/content-view";
import AppFooter from 'src/shared_modules/veu-visitors-core/components/footer';
import ChatProvider from './components/chat-provider';
import WallOfIdeasProvider from './components/wallOfIdeas/wallOfIdeasProvider';
import AppointmentsProvider from 'src/shared_modules/veu-visitors-core/components/appointments/provider';

import AppRedirect from 'src/shared_modules/veu-visitors-core/components/app-redirect';
import AppRouting from './components/app-routing';

import {
    showPopover,
    hidePopover
} from 'src/shared_modules/veu-visitors-core/actions/popovers';

import {
    clearChannelUrl,
    fetchChannelUrl,
    fetchRefreshAll
} from "src/shared_modules/veu-visitors-core/actions/event";

import {
    initiateSocket, disconnectSocket,
    subscribeToBackend
} from './socket';

import {
    currentLanguage,
} from "src/shared_modules/i18n";

import { withMatomo } from 'src/shared_modules/veu-visitors-core/components/common/matomo-tracking';

import { DOCTITLE_PREFIX } from "src/constants";

import './App.css';

/*
 * ---------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------
 * App React Component
 * ---------------------------------------------------------------------
 */
class App extends Component {
    constructor(props) {
        super(props);
        // local internal component state
        this.state = {
            isLoading: true,
            error: null,
            timerInstance: null,
            timerDelay: 30000, // 30 secs
            // disable logging when live
            logging: (process.env.REACT_APP_DEPLOY_ENV !== 'live'),
            message: ''
        };
    }

    componentDidMount() {
        this.handleTimeoutReached = this.handleTimeoutReached.bind(this);

        // timeout is allways needed since the user count needs also an update
        // first starts after 1 sec all other iterations are delayed by timerDelay above
        this.setState({
            timerInstance: setTimeout(() => { this.handleTimeoutReached() }, 1000)
        });

        let code2 = currentLanguage().code2;
        let evname = this.getValueForLang(this.props.event.name, code2);

        document.title = DOCTITLE_PREFIX + " " + evname;

        this.props.trackPageView();

        // socket test
        initiateSocket('visitor');
        subscribeToBackend((err, data) => {
            if (err) return;
            this.setState({ message: data });
        });

    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.location !== prevProps.location && prevProps.location) {
            this.props.trackPageView();
        }
    }

    componentWillUnmout() {
        if (this.state.timerInstance !== null) {
            clearTimeout(this.state.timerInstance);
        }

        // socket test
        disconnectSocket();
    }

    getValueForLang(obj, code2) {
        if (!obj) {
            return '';  // no event.
        }
        let val = obj.en.toString();
        if (obj[code2])
            val = obj[code2].toString();
        return val;
    }

    async handleTimeoutReached() {
        if (this.state.logging)
            console.log("app - handleTimeoutReached");

        // do not restart if any error occured!
        if (this.props.system.appState === "internal-error") {
            console.error("shutdown refresh due to an error");
            this.setState({ timerInstance: null });
            return;
        }
        // fetchTimeoutUpdate calls backend to get event
        await this.props.fetchRefreshAll();

        // check also after call
        if (this.props.system.appState === "internal-error") {
            console.error("shutdown refresh due to an error");
            return;
        }

        let nextDelay = this.state.timerDelay;
        if (this.props.event.refreshAllTimeout) {
            nextDelay = parseInt(this.props.event.refreshAllTimeout) * 1000;
        }

        if (this.state.logging)
            console.log("next Refresh in " + (nextDelay / 1000) + " sec");

        //start next timout delay
        this.setState({
            timerInstance: setTimeout(() => { this.handleTimeoutReached() }, nextDelay)
        })

        // FIXME disabled now replaxe with notistack this.checkForStreamPopups();
        if (this.props.user.loggedIn) {
            this.checkForConferencePopups();
        }

    }


    /*
    * Check for Conferences Popups to be shown
    */
    checkForConferencePopups = () => {

        // console.log("App - checkStreamsAndConferences");
        let ev = this.props.event;
        if (ev.hasConference === true) {
            if (ev.liveConference) {
                if (ev.liveConference.onAir) {
                    if (ev.channelurl) {
                        if (this.props.popovers.showReminderConference === false) {
                            this.props.showPopover("ReminderConference");
                        }
                    } else {
                        this.props.fetchChannelUrl();
                    }
                } else {
                    this.props.clearChannelUrl();

                    if (this.props.popovers.showReminderConference === true)
                        this.props.hidePopover("ReminderConference");
                }
            } else {
                this.props.clearChannelUrl();

                if (this.props.popovers.showReminderConference === true)
                    this.props.hidePopover("ReminderConference");
            }
        }
    };

    render() {
        return (<div className="App">
            <MainNav />
            <CookieOverlay />
            <PopoverProvider />
            <ContentView />
            <ChatProvider />
            <WallOfIdeasProvider />
            <AppointmentsProvider />

            <main id="stage">
                <AppRouting enablePlazz={this.props.event.hasPlazzConvention} />
            </main>
            <AppRedirect />

            <Hidden lgDown>
                <AppFooter />
            </Hidden>
            <DeployEnvFrame />
        </div>);
    }
}
/*
 * ---------------------------------------------------------------------
 * App prop Types
 * ---------------------------------------------------------------------
 */
App.propTypes = {
    popovers: PropTypes.object,
    event: PropTypes.object,
    system: PropTypes.object,
    user: PropTypes.object,

    woi: PropTypes.object,
};
/*
 * ---------------------------------------------------------------------
 * App Redux Container
 * ---------------------------------------------------------------------
 */
const mapStateToProps = (state, ownProps) => {
    return {
        //core
        popovers: state.popovers,
        event: state.event,
        system: state.system,
        user: state.user,
        /// plazz
        woi: state.plazz.woi,
    };
};

const mapDispatchToProps = {
    fetchRefreshAll,
    showPopover,
    hidePopover,
    clearChannelUrl,
    fetchChannelUrl,
    currentLanguage
};

/*
 * ---------------------------------------------------------------------
 * Export the redux container
 * ---------------------------------------------------------------------
 */
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(withMatomo(App)));
